import styled from 'styled-components';
import { error, grey50, white, grey70 } from 'constants/variables.js';
import toRem from 'utils/toRem';
import handleNestedId from '../lib/handleNestedId.ts';
import InputLabel from '../partials/InputLabel/index.tsx';
import InputError from '../partials/InputError/index.tsx';
import { ChevronIcon } from 'assets/icons';

export interface SelectItemProps {
  label: string;
  value: string | number;
}

export interface SelectProps {
  className?: string;
  variant?: string;
  id: string;
  label: string;
  placeholder?: string;
  selectItems: SelectItemProps[];
  value?: string | number;
  onChange?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  errors?: {
    [key: string]: { type: string; message: string };
  };
}

interface Disabled {
  disabled?: boolean;
}

const StyledSelectInputContainer = styled.div`
  position: relative;
`;

const StyledSelectInput = styled.select`
  border: 1px solid ${grey50};
  width: 100%;
  padding: ${toRem(12)};
  cursor: pointer;
  appearance: none; // To remove native arrow
  background-color: ${white};

  &:disabled {
    opacity: 0.3;
    pointer-events: none;
  }

  &.has-error {
    border-color: ${error};
  }
`;

const StyledIconContainer = styled.div<Disabled>`
  pointer-events: none;
  position: absolute;
  right: ${toRem(14)};
  top: 50%;
  transform: translateY(-50%);
  line-height: 0;
  ${({ disabled }) => disabled && 'opacity: 0.3;'};
`;

const Select = ({
  className,
  variant,
  id,
  label,
  placeholder = 'Please select an option',
  selectItems,
  value,
  onChange,
  onBlur,
  disabled,
  errors,
}: SelectProps) => {
  const fieldId = handleNestedId(id);
  const hasErrors = !!errors && !!Object.keys(errors).length;

  return (
    <div
      className={`cef-ui-select${variant ? ` cef-ui-select--${variant}` : ''}${
        className ? ` ${className}` : ''
      }`}>
      <InputLabel id={id} label={label} />

      <StyledSelectInputContainer>
        <StyledSelectInput
          className={`cef-ui-select__input${hasErrors ? ' has-error' : ''}`}
          id={id}
          name={id}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}>
          <option defaultValue="">{placeholder}</option>

          {selectItems.map(({ label: itemLabel, value: itemValue }) => (
            <option key={String(itemValue)} value={String(itemValue)}>
              {itemLabel}
            </option>
          ))}
        </StyledSelectInput>

        <StyledIconContainer disabled={disabled} aria-hidden>
          <ChevronIcon
            width={14}
            height={10}
            direction="down"
            fillColor={grey70}
          />
        </StyledIconContainer>
      </StyledSelectInputContainer>

      {hasErrors && fieldId && errors[fieldId] && (
        <InputError errorMessage={errors[fieldId].message} />
      )}
    </div>
  );
};

export default Select;
